import { DIALOG_DATA } from '@angular/cdk/dialog';
import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject } from '@angular/core';
import { InitialReasonV2, InitialReasonsDataService, PmsType } from '@ffd/api-client';
import { AddTreatmentModalService } from '@libs/charting';
import { AppointmentReason, selectSiteSettingsPmsType, selectUserSiteId } from '@libs/store';
import { ToastService } from '@libs/shared-services';
import { DataState, DISABLE_GLOBAL_ERROR_TOASTS, FfdComponentStore, Loadable } from '@libs/utils';
import { tapResponse } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { switchMap } from 'rxjs';

export interface ReasonSelectorModalData {
  selectedReasons: AppointmentReason[];
}

export interface ReasonSelectorModalState {
  initialReasons: Loadable<InitialReasonV2[]>;
  selectedReasons: AppointmentReason[];
}

export interface ReasonSelectorModalViewModel {
  initialReasons: Loadable<AppointmentReason[]>;
  selectedReasons: AppointmentReason[];
}

@Injectable()
export class ReasonSelectorModalService extends FfdComponentStore<ReasonSelectorModalState, ReasonSelectorModalViewModel> {
  public dialogData: ReasonSelectorModalData = inject(DIALOG_DATA);
  private readonly store = inject(Store);
  private readonly initialReasonsDataService = inject(InitialReasonsDataService);
  public readonly addTreatmentModalService = inject(AddTreatmentModalService);
  public readonly toastService = inject(ToastService);
  public readonly translateService = inject(TranslateService);

  public selectedReasons = this.selectSignal((state) => state.selectedReasons);

  private readonly createAppointmentSiteId = this.store.selectSignal(selectUserSiteId);

  private readonly pmsType = this.store.selectSignal(selectSiteSettingsPmsType);
  private readonly isOpenDental = computed(() => this.pmsType() === PmsType.OPEN_DENTAL);

  constructor() {
    super({ initialReasons: { state: DataState.Initial, data: [] }, selectedReasons: [] });
  }
  public override init(): void {
    if (!this.isOpenDental()) {
      this.getInitialReasons();
    }
    this.patchState(() => ({
      selectedReasons: this.dialogData.selectedReasons,
    }));
  }

  protected override mapStateToViewModel(state: ReasonSelectorModalState): ReasonSelectorModalViewModel {
    const reasonOptions =
      this.addTreatmentModalService.selectSignal(
        (state) =>
          state.treatments.data?.map((treatment) => ({
            code: treatment.dentalCode,
            abbreviation: treatment.ffdAbbrevDescription ?? '',
          })),
      )() ?? [];

    return {
      ...state,
      initialReasons: {
        state: state.initialReasons.state,
        data: state.initialReasons.data?.map((reason) => {
          const option = reasonOptions.find((r) => r.code === reason.code);
          return { code: reason.code, abbreviation: option?.abbreviation || reason.name, name: reason.name };
        }),
      },
    };
  }

  public addReason(reason: AppointmentReason): void {
    this.patchState((state) => ({
      selectedReasons: [...state.selectedReasons, reason],
    }));
  }

  public removeReason(code: string): void {
    this.patchState((state) => ({
      selectedReasons: state.selectedReasons.filter((reason) => !(reason.progressNoteId == null && reason.code === code)),
    }));
  }

  public removeReasonByProgressNoteId(progressNoteId: string): void {
    this.patchState((state) => ({
      selectedReasons: state.selectedReasons.filter((reason) => reason.progressNoteId !== progressNoteId),
    }));
  }
  public getInitialReasons = this.effect(($) => {
    this.patchState({ initialReasons: { data: [], state: DataState.Loading } });

    return $.pipe(
      switchMap(() => {
        return this.initialReasonsDataService
          .getInitialReasonsBySiteV2(this.createAppointmentSiteId() ?? '', undefined, undefined, {
            context: new HttpContext().set(DISABLE_GLOBAL_ERROR_TOASTS, true),
          })
          .pipe(
            tapResponse(
              (initialReasons) => {
                this.patchState({ initialReasons: { data: initialReasons.reasons, state: DataState.Fetched } });
              },
              (error: HttpErrorResponse) => {
                this.toastService.error(this.translateService.instant('error_message_server_error_initial_reasons'));
                return console.error(error);
              },
            ),
          );
      }),
    );
  });
}
